/* eslint-disable spaced-comment */
// swagger url at time of building https://gphub-dev.optum.com/api/gpappstateengine/swagger-ui/index.html
import { DateTime } from 'luxon';
import {
  existsOrBlank,
  fallback,
  conditionOrBlank,
} from './mapping-tools';
import { stripPhone } from './formatting';
import getAgentID from './getAgentID';

export default ({
  agent,
  quote,
  fields: res,
  plan,
  theme,
  confirmationNumber,
}) => {
  const { useCarrierPerms } = theme;

  const blankValue = undefined;

  const agentNPN = agent.npn;
  let agentID;
  if (useCarrierPerms) {
    agentID = getAgentID({ agent, quote }).agentID;
  } else {
    agentID = agentNPN;
  }

  const planYear = plan.PlanYear;

  // const isSepOther = res.sep_code === 'OTH';

  const gender = res.gender === 'male' ? 'MALE' : 'FEMALE';

  let race = blankValue;
  let ethnicity = blankValue;

  if (Array.isArray(res.race) && res.race.length) {
    race = res.race.join(',');
  }
  if (Array.isArray(res.ethnicity) && res.ethnicity.length) {
    ethnicity = res.ethnicity.join(',');
  }

  /*
    values for applicationInfo.electionPeriod.sepReasonCode from swagger
    01 - Government Entity - Declared Disaster or Other Emergency
    11 - CMS Termination
    12 - Non - renewals or Terminations
    21 - Accessible Formats
    22 - Involuntary Loss of Creditable Prescription Drug Coverage
    23 - Disenroll in Connection with a CMS Sanction
    24 - Disenrollment from Part D to Enroll in or Maintain Other Creditable Coverage
    25 - Involuntarily Disenrolled from an MA - PD plan due to loss of Part B
    26 - Disenrolling from OEPI
    27 - PACE
    28 - Cost Plans Non - renewing
    29 - Terminated a Medigap Policy
    30 - Chronic Care SNP
    31 - Institutionalized Individuals
    32 - Medicare Entitlement Determination Made Retroactively
    33 - SEP65
    34 - Enroll in Part B during the Part B (GEP)
    35 - Loss of special needs Status
    36 - Disenrolling from a Cost plan
    37 - Non - U.S. Citizens who become Lawfully Present
    38 - Qualified SPAP or Loss of SPAP eligibility
    39 - PLAN PLACED IN RECEIVERSHIP
    40 - CONSISTENT POOR PERFORMER PLAN
    41 - MA Only IEP2 - (Additional Part D)
    42 - MA FOR A/B EXCEPT COND ENROLL
    43 - PT D - A/B EXCEPT COND ENROLL
    90 - Not Adequately Informed about Creditable Prescription Drug Coverage
    91 - Significant Change in Provider Network
    92 - CONTRACT VIOLATION
    93 - Other Exceptional Circumstances
    94 - Insulin SEP
  */

  /*
    values for applicationInfo.electionPeriod.specialElectionReasonId from swagger
    2 - Change in permanent residence
    5 - Joining Employer Group
    7 - Qualified SPAP or Loss of SPAP eligibility
    9 - Institutionalized Individuals
    10 - Involuntary Loss of Creditable Prescription Drug Coverage
    13 - OEP to enroll in a PDP
    14 - MAOnlyIEP2
    17 - MAOnlyIEP2
    20 - Moved back to the U.S.
    21 - Loss of EGHP coverage
    22 - Terminated a Medigap Policy
    23 - PACE
    24 - Loss of special needs Status
    25 - Enroll in Part B during the Part B (GEP)
    26 - Non-renewals or Terminations
    27 - Medicare Entitlement Determination Made Retroactively
    29 - Chronic Care SNP
    31 - Cost Plans Non-renewing
    34 - Disenrollment from Part D to Enroll in or Maintain Other Creditable Coverage
    37 - Not Adequately Informed about Creditable Prescription Drug Coverage
    40 - MA Only IEP2- (Additional Part D)
    41 - Involuntarily Disenrolled from an MA-PD plan due to loss of Part B
    45 - Disenrolling from a Cost plan
    46 - Other Exceptional Circumstances
    48 - Five Star
    49 - Qualified SPAP or Loss of SPAP eligibility
    52 - Coordinating PDP
    54 - Significant Change in Provider Network
    55 - ICEP
    56 - ICEP DELAYED B
    57 - Non-U.S. Citizens who become Lawfully Present
    58 - OEP - Annual
    59 - OEP - Newly Eligible
    60 - Dual- and Other LIS-Eligible Individuals (before 1/1/2025)
    61 - Change in their Dual or LIS-Eligible Status
    62 - CMS and State-Initiated Enrollments
    63 - Incarcerated and have now been released
    64 - SEAMLESS CONVERSION
    65 - PLAN PLACED IN RECEIVERSHIP
    66 - CONSISTENT POOR PERFORMER PLAN
    67 - Disenrolling from OEPI
    68 - CMS Termination
    69 - Government Entity-Declared Disaster or Other Emergency
    71 - Accessible Formats
    72 - Disenroll in Connection with a CMS Sanction
    73 - CONTRACT VIOLATION
    74 - Insulin SEP
    75 - MA FOR A/B EXCEPT COND ENROLL
    76 - PT D - A/B EXCEPT COND ENROLL
    78 - Dual- and Other LIS-Eligible Individuals (on or after 1/1/2025)
  */

  /*
    electionType options:
    A - ANNUAL
    E - INITIAL (IEP)
    F - Additional IEP (IEP2)
    I - INITIAL COORDINATED ELECTION PERIOD (ICEP)
    J - SEAMLESS CONVERSION
    M - OPEN ENROLLMENT
    P - INTEGRATED CARE (SEP)
    L - DUAL (before 1/1/2025)
    Q - DUAL (on or after 1/1/2025)
    R - SEP FIVE STAR
    S - SPECIAL (SEP)
    T - OPEN ENROLLMENT PERIOD INSTITUTIONAL (OEPI)
    U - SEP AUTO AND FACILITATED
    V - SEP CHANGES IN RESIDENCE
    W - SEP EGHP
  */
  // const electionPeriodOpts = { AEP: 'A', SEP: 'S' };
  // const electionPeriod = electionPeriodOpts[res.enrolltype];
  // format sep date as mmddyyyy
  const sepDate = res.sep_date ? `${res.sep_date.slice(5, 7)}${res.sep_date.slice(8, 10)}${res.sep_date.slice(0, 4)}` : '';
  let sepConcat = `${res.sep_code} ${sepDate}`.trim();
  // per UHCs instruction on implementation call, during AEP they want this value 'AEP' sent in
  // applicationInfo.electionPeriod.specialElectionReasonFromOEC
  if (res.enrolltype === 'AEP') sepConcat = 'AEP';

  const shopperFullName = `${res.first_name} ${res.middle_name ? `${res.middle_name} ` : ''}${res.last_name}`;

  // they want the bank account holder's name split by first and last name. Handle multiple spaces
  // and more than two words in the name
  const splitNameArray = (res.eft_actname || '').trim().replace(/\s+/g, ' ').split(' ');
  const bankHolderFirstName = splitNameArray[0];
  const bankHolderLastName = splitNameArray.slice(1, splitNameArray.length).join(' ');

  // values the api accepts at time of writing 2025-02-26
  // SSA | DIRECT PAY | EFT
  let paymeth = blankValue;
  if (res.paymeth === 'ssi' || res.paymeth === 'rrb') paymeth = 'SSA';
  if (res.paymeth === 'direct') paymeth = 'DIRECT PAY';
  if (res.paymeth === 'eft') paymeth = 'EFT';

  // need to map form responses into this json array for CSNP prequal questions
  const isCSNP = plan.SNPIND === 1; // 1 = CSNP, 2 = DSNP, 3 = ISSNP
  const csnpPrequalQuestions = [];
  const prequalMap = [
    { formFieldName: 'snp_diabetes', questionnaireNumber: '1', type: 'DIABETES MELLITUS' },
    { formFieldName: 'snp_diabetes_insulin', questionnaireNumber: '2', type: 'DIABETES MELLITUS' },
    { formFieldName: 'snp_chf', questionnaireNumber: '1', type: 'CHRONIC HEART FAILURE' },
    { formFieldName: 'snp_chf_fluid', questionnaireNumber: '2', type: 'CHRONIC HEART FAILURE' },
    { formFieldName: 'snp_chf_weight', questionnaireNumber: '3', type: 'CHRONIC HEART FAILURE' },
    { formFieldName: 'snp_cvd', questionnaireNumber: '1', type: 'CARDIOVASCULAR DISORDERS' },
    { formFieldName: 'snp_cvd_circulation', questionnaireNumber: '2', type: 'CARDIOVASCULAR DISORDERS' },
    { formFieldName: 'snp_cvd_pain', questionnaireNumber: '3', type: 'CARDIOVASCULAR DISORDERS' },
    { formFieldName: 'snp_cvd_rx', questionnaireNumber: '4', type: 'CARDIOVASCULAR DISORDERS' },
    { formFieldName: 'snp_cvd_defib_or_pacemaker', questionnaireNumber: '5', type: 'CARDIOVASCULAR DISORDERS' },
    { formFieldName: 'snp_cvd_implants', questionnaireNumber: '6', type: 'CARDIOVASCULAR DISORDERS' },
  ];
  if (isCSNP) {
    prequalMap.forEach((question) => {
      const name = question.formFieldName;
      if (res[name] === true || res[name] === false) {
        csnpPrequalQuestions.push({
          applicantResponse: res[name],
          questionnaireNumber: question.questionnaireNumber,
          type: question.type,
        });
      }
    });
  }

  const formattedData = {
    // applicationCurrentState: 'NEW',
    applicationInfo: {
      accessibilityFormat: (res.altwritten || blankValue),
      applicationDate: DateTime.now().toISODate(),
      applicationReferenceNumber: confirmationNumber,
      applicationSource: 'OEC_PARTNER',
      // applicationVerified: true,
      // beqOverride: blankValue,
      broker: '123456', // agentID,
      brokerSignatureDate: DateTime.now().toISODate(),
      // denialReason: blankValue,
      // deniedOrWithdrawDate: blankValue,
      effectiveDate: res.effective_date,
      electionPeriod: {
        // effectiveDateChanged: blankValue,
        // electionPeriodOverride: blankValue,
        // electionType: electionPeriod, // this should never be sent
        // Only used when indicating OTH as the SEP Reason Code in order to explain the reason the individual is eligible to enroll
        // sepCMSReasonCodeFromOEC: isSepOther ? res.sep_other : blankValue, // don't send this for SEP
        // sepReasonCode: blankValue,
        // specialElectionReasonDate: (res.sep_date || blankValue), // don't send this
        specialElectionReasonFromOEC: sepConcat,
        // specialElectionReasonId: blankValue,
      },
      // employerAccountId: blankValue, // we don't collect
      enrollmentPlanYear: String(planYear),
      // ericksonInstCode: blankValue, // we don't collect
      // familyId: blankValue,
      // fileName: blankValue,
      // individualRepName: `${agent.firstName} ${agent.lastName}`,
      // isCallRequired: blankValue,
      // isDraftApplication: false, // I assume this should always be false if we're submitting enrollments?
      // isEarlyApplication: blankValue,
      // isEmployerSubsidyEnrollmentOverride: blankValue,
      // isEmployerSubsidyRejectionPreviouslyReceived: blankValue,
      // isForManualReview: blankValue,
      isPaperless: res.paperless_optin,
      // isSignatureDateOverride: blankValue,
      isSignaturePresence: true, // needs to be populated, this is UHCs 'beneficiary consent signature'
      // isSpap: blankValue,
      // isSpecialHandlingOverride: blankValue,
      // isTranslatorPresent: blankValue,
      // marketingCode: blankValue, // I assume we don't have marketing codes...
      medicaidInd: res.onmedicaid, // this is a bool in redux
      // nationalProducerNumber: agentNPN, // remove SOGI questions
      // notes: blankValue,
      partAStartDate: res.part_a_effective_date,
      // partAStopDate: blankValue, // we don't collect this
      partBStartDate: res.part_b_effective_date,
      // partBStopDate: blankValue, // we don't collect this
      // partDStartDate: blankValue, // we don't collect this
      // partDStopDate: blankValue, // we don't collect this
      planInfo: {
        // billGroupNumber: blankValue, // we don't collect this
        // branchNumber: blankValue, // we don't collect this
        contractNumber: plan.ExternalContractID,
        // dentalRider: blankValue, // we don't collect riders currently
        drugCoverageType: (plan.PlanTypeDesc || 'MAPD').toUpperCase(), // valid values are MA, MAPD, PDP, SSP
        // employerNumber: blankValue, // we don't collect this
        // fitnessRider: blankValue, // we don't collect this
        // gpsPlanCode: blankValue, // we don't collect this
        // groupId: blankValue, // we don't collect this
        // groupNumber: blankValue, // we don't collect this
        // marketProductName: blankValue, // we don't collect this
        // masterGroup: blankValue, // we don't collect this
        pbp: plan.ExternalPlanID,
        // planId: blankVAlue
        // plusRider: blankValue,
        // riderGroupId: blankValue, // we don't collect this
        // riderGroupNumber: blankValue, // we don't collect this
        // riderMasterGroup: blankValue, // we don't collect this
        segmentNumber: plan.ExternalSegmentID,
        // siteId: blankValue, // Not sure if we have a site id or not that should be populated?
        // wisconsinRider: blankValue,
      },
      // receivedMissingDate: blankValue, // we don't collect this
      // relationshipToTheEnrollee: existsOrNull(res.agent_relationship), // remove SOGI questions
      signatureDate: DateTime.now().toISODate(),
      // stampedReceivedDate: blankValue, // we don't collect this
      submitTime: DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss ZZZZ'),
      workStatus: (res.willwork || res.willworkspouse),
    },
    // applicationStateHistory: [
    //   {
    //     applicationState: blankValue,
    //     modifiedBy: blankValue,
    //     timeStamp: blankValue,
    //     workQueueReason: blankValue,
    //     workQueueType: blankValue,
    //   },
    // ],
    // audit: {
    //   createdBy: blankValue,
    //   createdOn: blankValue,
    //   modifiedBy: blankValue,
    //   modifiedOn: blankValue,
    // },
    authorizedRep: fallback(res.ispoa, {
      address: {
        addressLine1: conditionOrBlank(res.ispoa, res.poa_addr_street1, blankValue),
        // addressLine2: blankValue, // we don't collect this
        // addressType: blankValue,
        city: conditionOrBlank(res.ispoa, res.poa_addr_city, blankValue),
        // country: 'US', // why does this field even exist??
        stateCode: conditionOrBlank(res.ispoa, (res.poa_addr_state || '').toUpperCase(), blankValue),
        zipCode: conditionOrBlank(res.ispoa, res.poa_addr_zip, blankValue),
      },
      firstName: conditionOrBlank(res.ispoa, res.poa_fname, blankValue),
      lastName: conditionOrBlank(res.ispoa, res.poa_lname, blankValue),
      // middleName: blankValue, // we don't collect this
      phoneNumber: conditionOrBlank(res.ispoa, stripPhone(res.poa_tel), blankValue),
      type: conditionOrBlank(res.ispoa, 'AUTHORIZED REPRESENTATIVE', blankValue),
    }, undefined),
    // if plan is CSNP, populate this array with 1 object. If not, set an empty array on chronicCarInfo
    chronicCareInfo: fallback(isCSNP, [
      {
        applicantDOB: res.birthdate,
        applicantMBI: res.medicare_id,
        applicantName: shopperFullName,
        disclosure: [
          {
            // address: {
            //   addressLine1: blankValue,
            //   addressLine2: blankValue,
            //   addressType: blankValue,
            //   city: blankValue,
            //   country: 'US',
            //   stateCode: blankValue,
            //   zipCode: blankValue,
            // },
            applicantDOB: res.birthdate,
            applicantFullName: shopperFullName,
            applicantProviderFullName: existsOrBlank(res.snp_physician_name, blankValue),
            applicantProviderPhoneNumber: existsOrBlank(stripPhone(res.snp_physician_tel), blankValue),
            // name: blankValue,
            // phoneNumber: blankValue,
            // providerFormCertifiedCardiovascularDisorder: blankValue,
            // providerFormCertifiedChronicHeartFailure: blankValue,
            // providerFormCertifiedDiabetes: blankValue,
            // relationshipToApplicant: blankValue,
            // signatureDate: blankValue,
            // signaturePresence: blankValue,
            type: 'APPLICANT', // per UHC's direction this is always APPLICANT if we send a CSNP
          },
        ],
        preQualifyQuestions: csnpPrequalQuestions, // see above for generation code
        typeForm: 'PRE-ASSESSMENT', // this is always pre-assessment per UHC's direction
      },
    ], undefined),
    demographics: {
      dateOfBirth: res.birthdate,
      emailAddress: res.email,
      ethnicityCodes: ethnicity,
      // federalOrStateRecognizedTribe: blankValue, // we don't collect this
      firstName: res.first_name,
      gender,
      homePhoneNumber: stripPhone(res.home_phone),
      // homePhoneNumberExt: blankValue, // we don't collect this
      lastName: res.last_name,
      mbi: res.medicare_id,
      // medicaidNumber: existsOrBlank(res.medicaidnum, blankValue),
      middleName: res.middle_name,
      // mobilePhoneNumber: blankValue, // we don't collect mobile, just home
      // nameOfTribe: blankValue, // we don't collect this
      // originalMbi: blankValue, // we don't collect this. Swagger note: 'Original Medicare Beneficiary Identifier in case MBI is changed
      // prefix: blankValue, // we don't collect this
      raceCodes: race,
      // sogiGender: blankValue, // sogi is revoked for 2025
      // sogiSelfIdentify: blankValue, // sogi is revoked for 2025
      spokenLanguage: existsOrBlank(res.spokenlang, blankValue),
      // ssn: blankValue, // we don't collect this
      // telephoneConsumerProtectionActInd: blankValue, // we don't collect this?
      writtenLanguage: existsOrBlank(res.preflang, blankValue),
    },
    // householdId: blankValue,
    // id: blankValue, // I assume this is a UHC generated id so we shouldn't send?
    institutionalized: fallback(res.ltcf, {
      address: {
        addressLine1: existsOrBlank(res.ltcf_addr_street1, blankValue),
        addressLine2: existsOrBlank(res.ltcf_addr_street2, blankValue),
        // addressType: blankValue,
        city: existsOrBlank(res.ltcf_addr_city, blankValue),
        country: 'US',
        stateCode: existsOrBlank(res.ltcf_addr_state, blankValue),
        zipCode: existsOrBlank(res.ltcf_addr_zip, blankValue),
      },
      admitDate: existsOrBlank(res.ltcf_date, blankValue),
      // facilityId: blankValue,
      // isInstitutionalized: existsOrBlank(res.ltcf, blankValue), // don't need to send ltcf
      // mPinNumber: blankValue,
      name: existsOrBlank(res.ltcf_name, blankValue),
      phoneNumber: existsOrBlank(res.ltcf_tel, blankValue),
      // phoneNumberExt: blankValue,
    }, undefined),
    mailingAddress: {
      addressLine1: res.mailstreet1,
      addressLine2: res.mailstreet2,
      // addressType: blankValue,
      city: res.mailcity,
      // country: 'US', // why does this field exist...
      stateCode: res.mailstate,
      zipCode: res.mailzip,
    },
    paymentInfo: {
      bankAccount: fallback((res.paymeth === 'eft'), {
        accountNumber: res.eft_actnum,
        routingNumber: res.eft_routing,
        bankName: res.eft_bankname,
        firstName: bankHolderFirstName,
        lastName: bankHolderLastName,
        type: res.eft_acttype,
        authInd: res.eft_sigdiff,
      }, undefined),
      // creditCard: {},
      paymentMethodType: paymeth, // SSA | DIRECT PAY | EFT
    },
    permanentAddress: {
      addressLine1: res.street1,
      addressLine2: res.street2,
      // addressOverride: blankValue,
      // addressType: blankValue,
      // addressVerified: blankValue,
      city: res.city,
      // country: 'US', // why...?
      county: res.county_name,
      // fipsCountyCode: res.county,
      // fipsStateCode: blankValue,
      // ssaCountyCode: blankValue,
      // ssaStateCode: blankValue,
      stateCode: (res.state || '').toUpperCase(),
      zipCode: res.zipcode,
    },
    primaryCarePhysician: fallback(res.pcp_name, {
      // acceptAutoAssign: true,
      isCurrentPatient: existsOrBlank(res.pcp_established, blankValue),
      name: res.pcp_name,
      number: res.pcp_num,
      // originalPcpName: blankValue,
      // originalPcpNumber: blankValue,
      // pcpDataSource: 'BROKER_SALES',
      // pcpValidationResponse: blankValue,
      // preReasonDescription: blankValue,
      // providerOverride: blankValue,
    }, undefined),
    secondaryCoverage: fallback(res.othercoverage, {
      coverageInd: existsOrBlank(res.othercoverage, blankValue),
      nameOfCoverage: existsOrBlank(res.othercoverage_name, blankValue),
      // rxBinNumber: blankValue,
      rxGroup: existsOrBlank(res.othercoverage_group, blankValue),
      rxId: existsOrBlank(res.othercoverage_num, blankValue),
      // rxPCN: blankValue,
    }, undefined),
  };

  return { formattedData, agentID };
};
